import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit2, Trash2, Eye, EyeOff, FileText, Search } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_POST = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  cover_image_url: '',
  category: '',
  tags: [],
  is_published: false,
};

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 80);
}

export default function TenantBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenant/blog');
      setPosts(res.data.posts || []);
    } catch { toast.error('Gagal memuat artikel'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_POST);
    setTagInput('');
    setOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post.id);
    setForm({ ...post });
    setTagInput('');
    setOpen(true);
  };

  const handleTitleChange = (val) => {
    setForm(f => ({ ...f, title: val, slug: editing ? f.slug : slugify(val) }));
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm(f => ({ ...f, tags: [...f.tags, t] }));
    setTagInput('');
  };

  const removeTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Judul wajib diisi'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/tenant/blog/${editing}`, form);
        toast.success('Artikel diperbarui');
      } else {
        await api.post('/tenant/blog', form);
        toast.success('Artikel dibuat');
      }
      setOpen(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const togglePublish = async (post) => {
    try {
      await api.put(`/tenant/blog/${post.id}`, { is_published: !post.is_published });
      setPosts(ps => ps.map(p => p.id === post.id ? { ...p, is_published: !p.is_published } : p));
    } catch { toast.error('Gagal mengubah status'); }
  };

  const remove = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/tenant/blog/${id}`);
      setPosts(ps => ps.filter(p => p.id !== id));
      toast.success('Artikel dihapus');
    } catch { toast.error('Gagal menghapus'); }
    finally { setDeleting(null); }
  };

  const filtered = posts.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="tenant-blog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Blog</h1>
          <p className="text-sm text-slate-500 mt-1">{posts.length} artikel • Kelola konten blog toko Anda</p>
        </div>
        <Button onClick={openNew} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
          <Plus size={16} className="mr-2" /> Artikel Baru
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <Input
                placeholder="Cari judul atau kategori..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <FileText size={40} className="mx-auto mb-3 opacity-30" />
              <p>Belum ada artikel</p>
              <Button variant="link" onClick={openNew} className="mt-2">+ Buat artikel pertama</Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Judul</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Kategori</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Tayangan</th>
                  <th className="py-3 px-4 text-left font-medium text-slate-600">Status</th>
                  <th className="py-3 px-4 text-right font-medium text-slate-600">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map(post => (
                  <tr key={post.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-slate-900">{post.title}</p>
                        <p className="text-xs text-slate-400">/{post.slug}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-500">{post.category || '—'}</td>
                    <td className="py-3 px-4 text-slate-500">{post.view_count || 0}</td>
                    <td className="py-3 px-4">
                      <Badge className={post.is_published
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-600 border-slate-200'}>
                        {post.is_published ? 'Publikasi' : 'Draft'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title={post.is_published ? 'Jadikan Draft' : 'Publikasikan'} onClick={() => togglePublish(post)}>
                          {post.is_published ? <EyeOff size={15} className="text-amber-500" /> : <Eye size={15} className="text-emerald-500" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(post)}>
                          <Edit2 size={15} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(post.id)} disabled={deleting === post.id}>
                          <Trash2 size={15} className="text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Blog Editor Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Artikel' : 'Artikel Baru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Judul <span className="text-red-500">*</span></Label>
              <Input value={form.title} onChange={e => handleTitleChange(e.target.value)} placeholder="Judul artikel..." />
            </div>
            <div>
              <Label>Slug URL</Label>
              <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="judul-artikel" />
              <p className="text-xs text-slate-400 mt-1">Akan tampil sebagai: /blog/{form.slug || 'judul-artikel'}</p>
            </div>
            <div>
              <Label>Ringkasan / Excerpt</Label>
              <Textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} placeholder="Deskripsi singkat artikel..." />
            </div>
            <div>
              <Label>Konten</Label>
              <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={10} placeholder="Tulis konten artikel di sini..." className="font-mono text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gambar Cover (URL)</Label>
                <Input value={form.cover_image_url} onChange={e => setForm(f => ({ ...f, cover_image_url: e.target.value }))} placeholder="https://..." />
              </div>
              <div>
                <Label>Kategori</Label>
                <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Tips, Tutorial, Produk..." />
              </div>
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Tambah tag lalu tekan Enter" className="flex-1" />
                <Button type="button" variant="outline" onClick={addTag}>Tambah</Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.tags.map(t => (
                    <span key={t} className="flex items-center gap-1 bg-[#2D6CDF]/10 text-[#2D6CDF] rounded-full px-3 py-0.5 text-xs font-medium">
                      {t}
                      <button onClick={() => removeTag(t)} className="hover:text-red-500 ml-1 leading-none">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-700">Publikasikan</p>
                <p className="text-xs text-slate-500">Artikel akan tampil di blog toko</p>
              </div>
              <Switch checked={form.is_published} onCheckedChange={v => setForm(f => ({ ...f, is_published: v }))} />
            </div>
            {form.cover_image_url && (
              <img src={form.cover_image_url} alt="Cover preview" className="w-full h-40 object-cover rounded-lg border" onError={e => e.target.style.display = 'none'} />
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={save} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                {saving ? 'Menyimpan...' : editing ? 'Simpan Perubahan' : 'Buat Artikel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
